import { ColumnDef } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { Trash2 } from 'lucide-react';
import { Control, useFormContext, useWatch } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { MultiSelect } from '@/modules/common/components/molecules/selectors/multiselect';
import {
  SingleSelect,
  SingleSelectOption,
} from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { CustomAttributeType } from '@/modules/common/types/custom-attribute';

import { isPersistedId } from '../mappers/custom-attribute.mapper';
import {
  CustomAttributeOptionFormData,
  CustomAttributeRowFormData,
  CustomAttributesFormData,
  DUPLICATE_NAME_ERROR_KEY,
  NAME_REQUIRED_ERROR_KEY,
} from '../validations/custom-attribute-validation';

interface GetColumnsParams {
  onDelete: (attribute: CustomAttributeRowFormData, index: number) => void;
  control: Control<CustomAttributesFormData>;
  t: TFunction;
}

const I18N = 'pages.membersCustomAttributes';

/**
 * Editable cell for the attribute's option list.
 *
 * Both single- and multi-select attributes define a *list* of possible options, so the
 * cell always uses MultiSelect as a list editor: every defined option renders as a chip,
 * the creatable "Add X" affordance appends a new option, and removing a chip drops it.
 * The single/multi distinction is recorded solely by the row's `type`.
 *
 * Option ids are preserved across value round-trips: existing options keep their backend
 * id; newly typed options get a fresh negative temp id so the save diff can tell them apart.
 */
function OptionsCell({
  control,
  index,
  t,
}: {
  control: Control<CustomAttributesFormData>;
  index: number;
  t: TFunction;
}) {
  const { setValue } = useFormContext<CustomAttributesFormData>();
  const options = (useWatch({
    control,
    name: `attributes.${index}.options`,
  }) ?? []) as CustomAttributeOptionFormData[];

  // Lowest existing temp id minus one — keeps new draft option ids unique within the row.
  const nextTempOptionId = Math.min(0, ...options.map(o => o.id)) - 1;

  const selectOptions = options.map(o => ({ label: o.value, value: o.value }));
  const selectedValues = options.map(o => o.value);

  const idByValue = new Map(options.map(o => [o.value, o.id]));

  const applyValues = (values: string[]) => {
    let tempId = nextTempOptionId;
    const nextOptions: CustomAttributeOptionFormData[] = values.map(value => ({
      id: idByValue.get(value) ?? tempId--,
      value,
    }));
    setValue(`attributes.${index}.options`, nextOptions, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <FormField
      control={control}
      name={`attributes.${index}.options`}
      render={({ fieldState }) => (
        <FormItem className="w-full">
          <FormControl>
            {/*
              No `key` remount: MultiSelect re-syncs its internal selection from the
              `defaultValue` reference (which changes on every edit), so the dropdown
              stays open while the admin adds several options in a row.
            */}
            <MultiSelect
              options={selectOptions}
              defaultValue={selectedValues}
              onValueChange={applyValues}
              placeholder={t(`${I18N}.selectOptionsPlaceholder`)}
              isSelectAllButtonVisible={false}
              isCreatable
              onCreateOption={inputValue => ({ label: inputValue, value: inputValue })}
              optionItemVariant="removable"
              hideFooterWhenEmpty
              searchPlaceholder={t(`${I18N}.optionSearchPlaceholder`)}
              emptyMessage={t(`${I18N}.optionEmptyMessage`)}
              className={cn(fieldState.error && 'border-destructive')}
            />
          </FormControl>
          {fieldState.error && <FormMessage>{t(fieldState.error.message as string)}</FormMessage>}
        </FormItem>
      )}
    />
  );
}

export const getCustomAttributesColumns = ({
  onDelete,
  control,
  t,
}: GetColumnsParams): ColumnDef<CustomAttributeRowFormData>[] => {
  const typeOptions: SingleSelectOption[] = [
    { value: CustomAttributeType.Select, label: t(`${I18N}.typeSingle`) },
    { value: CustomAttributeType.MultiSelect, label: t(`${I18N}.typeMulti`) },
  ];

  return [
    {
      accessorKey: 'name',
      size: 240,
      minSize: 160,
      // Top-align every cell so that when one field shows an error message (which adds
      // height), the other cells' controls stay aligned at the top instead of recentering.
      meta: { cellClassName: 'align-top' },
      header: () => t(`${I18N}.nameColumn`),
      cell: ({ row }) => {
        const index = row.index;
        return (
          <FormField
            control={control}
            name={`attributes.${index}.name`}
            render={({ field, fieldState }) => {
              const errorKey = fieldState.error?.message;
              const isRequiredError = errorKey === NAME_REQUIRED_ERROR_KEY;
              const isDuplicateError = errorKey === DUPLICATE_NAME_ERROR_KEY;
              // Draft rows (negative id) that have never been typed into should not show
              // the "required" error until the user blurs the field — they were just added
              // via "Add attribute" and the footer tooltip communicates the requirement.
              // For all other cases (persisted rows, or draft rows after first interaction)
              // show the error as soon as the field becomes invalid (onChange mode).
              const isDraftRow = row.original.attributeId < 0;
              const showRequiredError =
                isRequiredError && (isDraftRow ? fieldState.isTouched : fieldState.isDirty);
              const showError = isDuplicateError || showRequiredError;
              return (
                <FormItem className="w-full">
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(`${I18N}.namePlaceholder`)}
                      className={cn(
                        showError && 'border-destructive focus-visible:ring-destructive'
                      )}
                    />
                  </FormControl>
                  {showError && <FormMessage>{t(errorKey!)}</FormMessage>}
                </FormItem>
              );
            }}
          />
        );
      },
    },
    {
      accessorKey: 'type',
      size: 200,
      minSize: 140,
      meta: { cellClassName: 'align-top' },
      header: () => t(`${I18N}.typeColumn`),
      cell: ({ row }) => {
        const index = row.index;
        // Type cannot be changed once persisted — the backend PATCH ignores it.
        const disabled = isPersistedId(row.original.attributeId);
        return (
          <SingleSelect
            name={`attributes.${index}.type`}
            control={control}
            options={typeOptions}
            placeholder={t(`${I18N}.typePlaceholder`)}
            searchable={false}
            isClearSelectionEnabled={false}
            disabled={disabled}
            className="w-full"
          />
        );
      },
    },
    {
      accessorKey: 'options',
      size: 420,
      minSize: 240,
      meta: { cellClassName: 'align-top' },
      header: () => t(`${I18N}.optionsColumn`),
      cell: ({ row }) => <OptionsCell control={control} index={row.index} t={t} />,
    },
    {
      id: 'actions',
      size: 80,
      minSize: 80,
      enableResizing: false,
      meta: { cellClassName: 'align-top' },
      header: () => <div className="flex w-full justify-end">{t(`${I18N}.actionsColumn`)}</div>,
      cell: ({ row }) => (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onDelete(row.original, row.index)}
            className="h-8 w-8"
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      ),
    },
  ];
};
